import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

import { I18nService } from '@recruitee/i18n';
import { RtError } from '@recruitee/utils';

import { WindowRef } from './common/window-ref';
import { NotificationRef, NotificationsController } from './notifications/notifications.module';

export class ClipboardCopyUnavailableError extends RtError {
  constructor() {
    super(
      'ClipboardCopyUnavailableError',
      'ClipboardCopy is not supported (createRange or execCommand doesnt exists)',
    );
  }
}

@Injectable({ providedIn: 'root' })
export class ClipboardCopy {
  // If it is not available and you will try to invoke fromString/fromHtmlElement,
  // it will throw, you should verify it by yourself or wrap invocations of this class methods
  // in try-catch block

  private isAvailable: boolean =
    this.document.createRange != null && this.document.execCommand != null;
  private defaultMessage: string;
  private copiedTextsBuffer: string[] = [];
  private notificationRef: NotificationRef | null = null;

  private readonly sourceElement: HTMLDivElement;

  constructor(
    private i18n: I18nService,
    private notificationsCtrl: NotificationsController,
    private window: WindowRef,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.defaultMessage = this.i18n.translate('rtu.common.copied');

    if (!this.isAvailable) {
      this.fromString = () => {
        throw new ClipboardCopyUnavailableError();
      };
      this.fromHtmlElement = () => {
        throw new ClipboardCopyUnavailableError();
      };
    } else {
      this.sourceElement = this.createSourceElement();
    }
  }

  public fromString(string: string, notificationTitle?: string): true {
    // update source element
    this.sourceElement.innerText = string;
    // select node contents
    this.document.createRange().selectNodeContents(this.sourceElement);

    // copy node contents to clipboard
    this.fromHtmlElement(this.sourceElement, notificationTitle);

    return true;
  }

  public fromHtmlElement(
    element: HTMLElement,
    notificationTitle: string = this.defaultMessage,
  ): true {
    let copiedText: string;

    if (['rt-input', 'rt-textarea'].includes(element.tagName.toLowerCase())) {
      (<HTMLInputElement>element.querySelector('input, textarea')).select();
      copiedText = this.window.nativeWindow.getSelection()!.toString();
    } else {
      const range = this.document.createRange();
      const selection = this.window.nativeWindow.getSelection()!;

      range.selectNodeContents(element);
      selection.removeAllRanges();
      selection.addRange(range);

      copiedText = selection.toString();
    }

    this.document.execCommand('copy');
    if (!this.copiedTextsBuffer.includes(copiedText)) {
      if (this.notificationRef) {
        this.notificationRef.close();
      }

      this.notificationRef = this.notificationsCtrl.success({ title: notificationTitle });
      this.notificationRef.afterClosed.subscribe(() => {
        this.copiedTextsBuffer = this.copiedTextsBuffer.filter(text => text !== copiedText);
      });

      this.copiedTextsBuffer = [...this.copiedTextsBuffer, copiedText];
    }

    return true;
  }

  private createSourceElement(): HTMLDivElement {
    const el = this.document.createElement('div');
    el.id = 'clipboard-copy-source';
    el.style.opacity = '0';
    el.style.position = 'absolute';
    el.style.right = '-99999px';
    this.document.body.appendChild(el);
    return el;
  }
}
